import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} overlay
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} error
 */

/** @extends {Component<Refs>} */
class LockedContent extends Component {
  requiredRefs = ['overlay', 'form', 'input', 'submitButton', 'error'];

  get #storageKey() {
    return `locked-content-unlocked-${this.dataset.sectionId}`;
  }

  get #password() {
    return this.dataset.password?.trim() ?? '';
  }

  get #remember() {
    return this.dataset.remember === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode) return;
    if (!this.#password) return;

    this.refs.submitButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.#checkPassword();
    });
    this.refs.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.#checkPassword();
      }
    });
    this.refs.input.addEventListener('input', () => this.handleInput());

    try {
      if (this.#remember && sessionStorage.getItem(this.#storageKey) === '1') {
        return;
      }
    } catch (e) {
      // sessionStorage blocked — fall through and show overlay.
    }

    this.refs.overlay.hidden = false;
    requestAnimationFrame(() => this.refs.input.focus());
  }

  handleInput() {
    if (!this.refs.error.hidden) {
      this.refs.error.hidden = true;
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    this.#checkPassword();
  }

  #checkPassword() {
    if (this.refs.input.value.trim().toLowerCase() === this.#password.toLowerCase()) {
      this.#unlock();
    } else {
      this.refs.error.hidden = false;
      this.refs.input.select();
    }
  }

  #unlock() {
    this.refs.overlay.hidden = true;
    this.refs.error.hidden = true;

    try {
      if (this.#remember) {
        sessionStorage.setItem(this.#storageKey, '1');
      }
    } catch (e) {
      // Storage unavailable — unlock still works for this page view.
    }
  }
}

if (!customElements.get('locked-content')) {
  customElements.define('locked-content', LockedContent);
}
